import * as React from "react";
import { Badge, useToast } from "@jhool-io/fe-components";
import { Link } from "react-router-dom";
import ActionButton from "../../../../components/ActionButton/ActionButton";
import AlertTriangleIcon from "../../../../components/Icons/AlertTriangle";
import CopyIcon from "../../../../components/Icons/Copy";
import ListState from "../../../../components/ListState/ListState";
import {
    copyToClipboard,
    formatDate,
    getSessionsNeedingConsent,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import useCurrentClientProfile from "../../../../hooks/useCurrentClient";
import {
    IPractice,
    IPracticeActiveSession,
    IPracticeHeldSession,
} from "../../../auth/types/auth.types";
import { useFetchUserPractices } from "../../../auth/hooks/auth.queries";
import {
    FormStatus,
    PORTAL_SUPPORTED_FORM_TYPES,
} from "../../../client-forms/types/client-forms.types";
import {
    getStatusDisplay,
    getStatusColors,
} from "../../../client-forms/helpers/client-forms.helpers";

function getConsentStatusText(status: FormStatus | undefined): string {
    if (status === FormStatus.COMPLETED) return "Consent completed";
    if (
        status === FormStatus.RECEIVED ||
        status === FormStatus.PAUSED ||
        status === FormStatus.CANCELLED
    )
        return `Consent ${getStatusDisplay(status)}`;
    return "Consent must be submitted for review. It will be marked completed after review.";
}

function isConsentResolved(status: FormStatus | undefined): boolean {
    return (
        status === FormStatus.COMPLETED ||
        status === FormStatus.RECEIVED ||
        status === FormStatus.PAUSED ||
        status === FormStatus.CANCELLED
    );
}

export default function SessionsActive() {
    const [isInfoCopied, setIsInfoCopied] = React.useState(false);

    const currentProfile = useCurrentClientProfile();
    const { data: userPractices } = useFetchUserPractices(
        currentProfile?.userId ?? ""
    );
    const { toast } = useToast();

    const selectedPractice = userPractices?.data?.practices.find(
        (practice: IPractice) =>
            practice.practice_id === currentProfile?.practice.practice_id
    );

    const sessionsNeedingConsentIds = new Set(
        (
            getSessionsNeedingConsent(
                selectedPractice?.active_sessions ?? []
            ) as IPracticeActiveSession[]
        ).map((s) => s.session_id)
    );

    const heldSessionsNeedingConsentIds = new Set(
        (
            getSessionsNeedingConsent(
                selectedPractice?.held_sessions ?? []
            ) as IPracticeHeldSession[]
        ).map((s) => s.held_session_id)
    );

    const hasAnySessions =
        (selectedPractice?.active_sessions?.length ?? 0) > 0 ||
        (selectedPractice?.held_sessions?.length ?? 0) > 0;

    return (
        <div className="mt-16 rounded-r8 border border-white/70 bg-white/95 p-16 shadow-sm">
            <div className="mb-12">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
                    All appointments
                </p>
                <p className="mt-4 text-sm text-muted">
                    Every appointment with provider details, recurrence, and
                    next scheduled date.
                </p>
                <Link
                    to="/session-resources#cancellation-policy"
                    className="mt-4 inline-block text-xs font-medium text-primary underline underline-offset-2"
                >
                    View our cancellation policy
                </Link>
            </div>

            {hasAnySessions ? (
                <div className="space-y-6">
                    {(selectedPractice?.held_sessions ?? []).map((session) => {
                        const needsConsent = heldSessionsNeedingConsentIds.has(
                            session.held_session_id
                        );
                        const consentStatus = session.consent?.status;
                        const consentCompleted =
                            isConsentResolved(consentStatus);
                        const consentStatusText =
                            getConsentStatusText(consentStatus);

                        return (
                            <div
                                key={session.held_session_id}
                                className="rounded-r8 border border-stroke-subtle bg-surface-raised px-12 py-10"
                            >
                                <div className="flex justify-between items-center flex-wrap gap-8">
                                    <div className="flex flex-col">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge
                                                content={
                                                    <span className="capitalize">
                                                        {removeEnumUnderscore(
                                                            session.service
                                                        )}
                                                    </span>
                                                }
                                                bgColor="#E8F1F4"
                                                textColor="var(--color-primary)"
                                            />
                                            <Badge
                                                content={
                                                    <span className="capitalize">
                                                        {removeEnumUnderscore(
                                                            session.delivery_option
                                                        )}
                                                    </span>
                                                }
                                                bgColor="#F7FAFC"
                                                textColor="#475467"
                                            />
                                            {session.consent &&
                                            !consentCompleted ? (
                                                <Badge
                                                    content={`Consent ${getStatusDisplay(
                                                        session.consent.status
                                                    )}`}
                                                    textColor={
                                                        getStatusColors(
                                                            session.consent
                                                                .status
                                                        ).textColor
                                                    }
                                                    bgColor={
                                                        getStatusColors(
                                                            session.consent
                                                                .status
                                                        ).bgColor
                                                    }
                                                />
                                            ) : null}
                                        </div>
                                        <p className="flex gap-2 mt-2 text-sm font-semibold text-black">
                                            <span>
                                                {session.provider.first_name}
                                            </span>
                                            {session.provider.last_name}
                                            {session.provider.credential
                                                ? ` · ${session.provider.credential}`
                                                : ""}
                                        </p>

                                        <div className="flex items-center flex-wrap text-xs text-muted">
                                            {session.provider.email ? (
                                                <a
                                                    href={`mailto:${session.provider.email}`}
                                                    className="underline mr-2"
                                                >
                                                    {session.provider.email}
                                                </a>
                                            ) : null}
                                            {session.provider
                                                .contact_info_client ? (
                                                <div>
                                                    {session.provider.email
                                                        ? "• "
                                                        : ""}
                                                    {
                                                        session.provider
                                                            .contact_info_client
                                                    }
                                                </div>
                                            ) : null}
                                            <ActionButton
                                                onClick={() => {
                                                    copyToClipboard(
                                                        `${session.provider.email} ${session.provider.contact_info_client}`
                                                    );
                                                    setIsInfoCopied(
                                                        !isInfoCopied
                                                    );
                                                    toast({
                                                        mode: "success",
                                                        message:
                                                            "Provider details copied!",
                                                        duration: 4000,
                                                    });
                                                }}
                                                size="auto"
                                                variant="normal"
                                                className="ml-2"
                                                actionTitle="Copy details"
                                            >
                                                <CopyIcon />
                                            </ActionButton>
                                        </div>

                                        {session.delivery_option ===
                                            "in_person" &&
                                        session.location?.location_name ? (
                                            <span className="flex items-center flex-wrap gap-4 text-xs text-muted">
                                                <svg
                                                    className="mt-[2px] h-12 w-12 shrink-0 text-primary"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                >
                                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                                    <circle
                                                        cx="12"
                                                        cy="10"
                                                        r="3"
                                                    />
                                                </svg>
                                                {`${session.location.office_name ? session.location.office_name : ""}  ${session.location.office_name ? `,${session.location.location_name}` : session.location.location_name}`}
                                            </span>
                                        ) : null}

                                        {session.consent ? (
                                            <div className="flex flex-col gap-1">
                                                <span
                                                    className={`text-xs font-medium ${consentCompleted ? "text-primary" : "text-danger-strong"}`}
                                                >
                                                    {consentStatusText}
                                                </span>
                                                {session.consent
                                                    ?.consent_form_link &&
                                                import.meta.env
                                                    .VITE_NODE_ENV !==
                                                    "production" &&
                                                !needsConsent ? (
                                                    <a
                                                        href={`/forms/consent/${session.booking_id}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-xs font-medium text-primary underline underline-offset-2"
                                                    >
                                                        View consent form
                                                    </a>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        {needsConsent && (
                                            <div className="mt-4 flex items-center justify-between flex-wrap gap-8 rounded-r8 border border-warning-stroke bg-warning-surface px-4 py-4 max-w-fit">
                                                <div className="flex items-center gap-2">
                                                    <AlertTriangleIcon stroke="var(--color-warning-text)" />
                                                    <p className="text-xs font-medium text-warning-strong">
                                                        Action required: Please
                                                        fill out your consent
                                                        form before your
                                                        session.
                                                        <a
                                                            href={
                                                                session.consent
                                                                    .consent_form_type &&
                                                                session.booking_id &&
                                                                PORTAL_SUPPORTED_FORM_TYPES.has(
                                                                    session
                                                                        .consent
                                                                        .consent_form_type
                                                                )
                                                                    ? `/forms/consent/${session.booking_id}`
                                                                    : session
                                                                          .consent
                                                                          .consent_form_link
                                                            }
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="ml-2 shrink-0 text-xs font-semibold text-primary underline underline-offset-2"
                                                        >
                                                            Fill out form
                                                        </a>
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col">
                                        <div className="text-xs text-muted">
                                            {session.recurrence_rule ? (
                                                <span className="flex items-center gap-2 capitalize">
                                                    <svg
                                                        className="h-12 w-12 shrink-0"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <polyline points="17 1 21 5 17 9" />
                                                        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                                                        <polyline points="7 23 3 19 7 15" />
                                                        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                                                    </svg>
                                                    {removeEnumUnderscore(
                                                        session.recurrence_rule
                                                    )}
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="text-xs text-muted">
                                            {session.start_date ? (
                                                <span className="flex items-center gap-2">
                                                    <svg
                                                        className="h-12 w-12 shrink-0"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <rect
                                                            x="3"
                                                            y="4"
                                                            width="18"
                                                            height="18"
                                                            rx="2"
                                                            ry="2"
                                                        />
                                                        <line
                                                            x1="16"
                                                            y1="2"
                                                            x2="16"
                                                            y2="6"
                                                        />
                                                        <line
                                                            x1="8"
                                                            y1="2"
                                                            x2="8"
                                                            y2="6"
                                                        />
                                                        <line
                                                            x1="3"
                                                            y1="10"
                                                            x2="21"
                                                            y2="10"
                                                        />
                                                    </svg>
                                                    Start:
                                                    {formatDate(
                                                        session.start_date,
                                                        false,
                                                        "EEE, MMM d"
                                                    )}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {selectedPractice?.active_sessions.map((session) => {
                        const needsConsent = sessionsNeedingConsentIds.has(
                            session.session_id
                        );
                        const activeConsentStatus = session.consent?.status;
                        const activeConsentCompleted =
                            isConsentResolved(activeConsentStatus);
                        const activeConsentStatusText =
                            getConsentStatusText(activeConsentStatus);

                        return (
                            <div
                                key={session.session_id}
                                className="rounded-r8 border border-stroke-subtle bg-surface-raised px-12 py-10"
                            >
                                <div className="flex justify-between items-center flex-wrap gap-8">
                                    <div className="flex flex-col">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge
                                                content={
                                                    <span className="capitalize">
                                                        {removeEnumUnderscore(
                                                            session.service
                                                        )}
                                                    </span>
                                                }
                                                bgColor="#E8F1F4"
                                                textColor="var(--color-primary)"
                                            />
                                            <Badge
                                                content={
                                                    <span className="capitalize">
                                                        {removeEnumUnderscore(
                                                            session.delivery_option
                                                        )}
                                                    </span>
                                                }
                                                bgColor="#F7FAFC"
                                                textColor="#475467"
                                            />
                                        </div>
                                        <p className="flex gap-2 mt-2 text-sm font-semibold text-black">
                                            <span>
                                                {session.provider.first_name}
                                            </span>
                                            {session.provider.last_name}

                                            {session.provider.credential
                                                ? ` · ${session.provider.credential}`
                                                : ""}
                                        </p>

                                        <div className="flex items-center flex-wrap text-xs text-muted">
                                            {session.provider.email ? (
                                                <a
                                                    href={`mailto:${session.provider.email}`}
                                                    className="underline mr-2"
                                                >
                                                    {session.provider.email}
                                                </a>
                                            ) : null}
                                            {session.provider
                                                .contact_info_client ? (
                                                <div>
                                                    {session.provider.email
                                                        ? "• "
                                                        : ""}
                                                    {
                                                        session.provider
                                                            .contact_info_client
                                                    }
                                                </div>
                                            ) : null}
                                            <ActionButton
                                                onClick={() => {
                                                    copyToClipboard(
                                                        `${session.provider.email} ${session.provider.contact_info_client}`
                                                    );
                                                    setIsInfoCopied(
                                                        !isInfoCopied
                                                    );
                                                    toast({
                                                        mode: "success",
                                                        message:
                                                            "Provider details copied!",
                                                        duration: 4000,
                                                    });
                                                }}
                                                size="auto"
                                                variant="normal"
                                                className="ml-2"
                                                actionTitle="Copy details"
                                            >
                                                <CopyIcon />
                                            </ActionButton>
                                        </div>

                                        {session.delivery_option ===
                                            "in_person" &&
                                        session.location?.location_name ? (
                                            <span className="flex items-center flex-wrap gap-4 text-xs text-muted">
                                                <svg
                                                    className="mt-[2px] h-12 w-12 shrink-0 text-primary"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                >
                                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                                    <circle
                                                        cx="12"
                                                        cy="10"
                                                        r="3"
                                                    />
                                                </svg>
                                                {`${
                                                    session.location.office_name
                                                        ? session.location
                                                              .office_name
                                                        : ""
                                                }  ${session.location.office_name ? `,${session.location.location_name}` : session.location.location_name}`}
                                            </span>
                                        ) : null}

                                        {session.consent ? (
                                            <div className="flex flex-col gap-1">
                                                <span
                                                    className={`text-xs font-medium ${
                                                        activeConsentCompleted
                                                            ? "text-primary"
                                                            : "text-danger-strong"
                                                    }`}
                                                >
                                                    {activeConsentStatusText}
                                                </span>
                                                {session.consent
                                                    ?.consent_form_link &&
                                                import.meta.env
                                                    .VITE_NODE_ENV !==
                                                    "production" &&
                                                !needsConsent ? (
                                                    <a
                                                        href={`/forms/consent/${session.booking_id}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-xs font-medium text-primary underline underline-offset-2"
                                                    >
                                                        View consent form
                                                    </a>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        {needsConsent && (
                                            <div className="mt-4 flex items-center justify-between flex-wrap gap-8 rounded-r8 border border-warning-stroke bg-warning-surface px-4 py-4 max-w-fit">
                                                <div className="flex items-center gap-2">
                                                    <AlertTriangleIcon stroke="var(--color-warning-text)" />
                                                    <p className="text-xs font-medium text-warning-strong">
                                                        Action required: Please
                                                        fill out your consent
                                                        form before your
                                                        session.
                                                        <a
                                                            href={
                                                                session.consent
                                                                    .consent_form_type &&
                                                                session.booking_id &&
                                                                PORTAL_SUPPORTED_FORM_TYPES.has(
                                                                    session
                                                                        .consent
                                                                        .consent_form_type
                                                                )
                                                                    ? `/forms/consent/${session.booking_id}`
                                                                    : session
                                                                          .consent
                                                                          .consent_form_link
                                                            }
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="ml-2 shrink-0 text-xs font-semibold text-primary underline underline-offset-2"
                                                        >
                                                            Fill out form
                                                        </a>
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col">
                                        {session.fee ? (
                                            <div className="flex items-center">
                                                <span className="text-sm font-semibold text-primary">
                                                    {showMoneyInAppFormat(
                                                        session.fee
                                                    )}
                                                </span>
                                                <span className="text-xs text-muted">
                                                    /session
                                                </span>
                                            </div>
                                        ) : null}

                                        <div className="text-xs text-muted">
                                            {session.recurrence_rule ? (
                                                <span className="flex items-center gap-2 capitalize">
                                                    <svg
                                                        className="h-12 w-12 shrink-0"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <polyline points="17 1 21 5 17 9" />
                                                        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                                                        <polyline points="7 23 3 19 7 15" />
                                                        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                                                    </svg>
                                                    {removeEnumUnderscore(
                                                        session.recurrence_rule
                                                    )}
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="text-xs text-muted">
                                            {session.next_session_date ? (
                                                <span className="flex items-center gap-2">
                                                    <svg
                                                        className="h-12 w-12 shrink-0"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <rect
                                                            x="3"
                                                            y="4"
                                                            width="18"
                                                            height="18"
                                                            rx="2"
                                                            ry="2"
                                                        />
                                                        <line
                                                            x1="16"
                                                            y1="2"
                                                            x2="16"
                                                            y2="6"
                                                        />
                                                        <line
                                                            x1="8"
                                                            y1="2"
                                                            x2="8"
                                                            y2="6"
                                                        />
                                                        <line
                                                            x1="3"
                                                            y1="10"
                                                            x2="21"
                                                            y2="10"
                                                        />
                                                    </svg>
                                                    Next:
                                                    {formatDate(
                                                        session.next_session_date,
                                                        false,
                                                        "EEE, MMM d"
                                                    )}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <ListState
                    emptyMessage="No appointments yet"
                    stateHelperText="Your appointments will appear here once they've been set. If you have any questions, please contact your therapist."
                    isError={false}
                />
            )}
        </div>
    );
}
